import { isNone } from "@ember/utils"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditMove } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course, CourseCalendarDateCustom } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { createTransferrableDate } from "@shared/models/transferrable-date"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  simpleInsertRange,
} from "cc-frontend/lib/unit-utils"
import { filterCourseDateCustom, isCourseDateCustom } from "cc-frontend/utils/filter-dates/course"
import { cloneDeep, find, includes, isNil, keyBy, map, partial } from "lodash-es"
import moment from "moment"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  dateString: string
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
}

export default function pullLessonBackwardsAction(payload: Payload): PreparedAction {
  return {
    name: "PULL_LESSON_BACKWARDS",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  let schoolDays = payload.rotationCalendar.attributes.schoolDays

  let consolidatedDatesOff = consolidateCourseDatesOff(
    payload.course,
    payload.planbook,
    payload.rotationCalendar,
  )

  const dates = cloneDeep(payload.course.attributes.calendar.dates)

  // If there is a lesson on the target date, delete it.
  const targetDate = dates.find(
    (date): date is CourseCalendarDateCustom =>
      isCourseDateCustom(date) && date.attributes.dateString === payload.dateString,
  )
  if (targetDate) {
    targetDate.attributes.cardStackId = null
    if (targetDate.attributes.unitEnd.length > 0) {
      // Also move its unitEnds back by one course-date-custom, to prevent overlapping units
      // (we don't want the target date to end its existing unit and also start a pulled-back unit).
      // Our caller's doesCourseDateHaveTinyUnit check should ensure that previousDate exists.
      let previousDate: CourseCalendarDateCustom | null = null
      for (const date of dates) {
        if (
          isCourseDateCustom(date) &&
          date.attributes.dateString < payload.dateString &&
          (!previousDate || date.attributes.dateString >= previousDate.attributes.dateString)
        ) {
          previousDate = date
        }
      }
      if (previousDate) {
        previousDate.attributes.unitEnd = previousDate.attributes.unitEnd.concat(
          targetDate.attributes.unitEnd,
        )
        targetDate.attributes.unitEnd = []
      }
    }
  }

  let courseDateTrainCarStarts = find(filterCourseDateCustom(payload.course), (date) => {
    return date.attributes.dateString > payload.dateString && !isNil(date.attributes.cardStackId)
  })
  let { transferrableArray, courseDatesWithoutTrainCar } = extractTrainCar(
    courseDateTrainCarStarts?.attributes.dateString,
    dates,
    consolidatedDatesOff,
    schoolDays,
  )

  let newStartDate = payload.dateString

  let newCourseDates = simpleInsertRange(
    transferrableArray,
    newStartDate,
    courseDatesWithoutTrainCar,
    consolidatedDatesOff,
    schoolDays,
  )
  let ret = constructUnitsAndDates(newCourseDates, consolidatedDatesOff, schoolDays)
  let { courseDates, units } = ret
  let patch = Patch.create("course", payload.course.id)
    .set(`attributes.calendar.dates`, courseDates)
    .set(`attributes.calendar.units`, units)
    .inc(`attributes.calendar.version`, 1).patch

  return [patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  let patch = Patch.create("course", payload.course.id)
    .set(
      "attributes.calendar.dates",
      JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates)),
    )
    .set(
      "attributes.calendar.units",
      JSON.parse(JSON.stringify(payload.course.attributes.calendar.units)),
    )
    .inc("attributes.calendar.version", 1).patch
  return [patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  let courseDate = find(filterCourseDateCustom(payload.course), (date) => {
    return date.attributes.dateString > payload.dateString && !isNil(date.attributes.cardStackId)
  })

  let cardStackId = courseDate?.attributes.cardStackId ? courseDate.attributes.cardStackId : null

  let cardStack = cardStackId ? findFn("card-stack", cardStackId) : null

  let lessonTitle = cardStack ? cardStack.attributes.title : "[NEW LESSON]"
  let originalDate = payload.dateString
  let formattedDateString = moment(originalDate).format("ddd, MMM. D, YYYY")

  let description = isNone(cardStackId)
    ? `Shifted lessons backwards onto ${formattedDateString}`
    : `Deleted "${lessonTitle}" from ${formattedDateString} and shifted lessons backwards`

  let newEvent: LessonEditMove = {
    ...createBlankEvent(),
    root_id: payload.planbook.id,
    root_type: "planbook",
    date_plan_scheduled_for: payload.dateString,
    primary_object_id: cardStackId,
    primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    primary_verb: "move",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, payload.planbook.id),
    primary_object_type: "lesson",
    primary_object_specifier: null,

    adverbial_verb: "bumping",
    adverbial_object_type: "lesson",
    adverbial_object_specifier: "backward",
    adverbial_object_id: cardStackId,
    adverbial_possessive: null,
    adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
  }

  return {
    title: "Pulled Next Lessons Backward",
    titleForAnalytics: "Lesson - Pulled lessons backwards",
    description: description,
    context: {
      lessonTitle: lessonTitle,
      courseTitle: payload.course.attributes.title,
      planbookTitle: payload.planbook.attributes.title,
      cardStackId: cardStackId,
      courseId: payload.course.id,
      planbookId: payload.planbook.id,
    },
    activity: {
      actor: `user-${userId}`,
      verb: "edited",
      object: `course-${payload.course.id}`,
      to: [
        `course-edits:course-${payload.course.id}`,
        `planbook-edits:planbook-${payload.planbook.id}`,
      ],
    },
    newAnalyticsEvents: [newEvent],
  }
}

function formatDateAsISO(date) {
  return dateFns.format(date, "YYYY-MM-DD")
}

function extractTrainCar(dateString, courseDates, datesOff, schoolDays) {
  let date = dateFns.parse(dateString)

  let courseDateMap = keyBy(courseDates, "attributes.dateString")
  let iterations = 0
  let isIterating = true
  let transferrableArray: Array<any> = []
  let modifiedDates = {}

  while (isIterating && iterations < 500) {
    iterations++

    let dateString = formatDateAsISO(date)

    if (!includes(schoolDays, dateFns.getDay(date))) {
      date = dateFns.addDays(date, 1)
      continue
    }

    if (includes(datesOff, dateString)) {
      date = dateFns.addDays(date, 1)
      continue
    }

    let courseDate = courseDateMap[dateString] ? courseDateMap[dateString] : undefined

    if (courseDate === undefined) {
      isIterating = false
      continue
    }

    if (
      courseDate.attributes.cardStackId === null &&
      courseDate.attributes.unitStart.length === 0
    ) {
      isIterating = false
      continue
    }

    transferrableArray.push(
      createTransferrableDate({
        cardStackId: courseDate.attributes.cardStackId,
        unitStart: courseDate.attributes.unitStart,
        unitEnd: courseDate.attributes.unitEnd,
      }),
    )

    modifiedDates[dateString] = cloneDeep(courseDate)
    modifiedDates[dateString].attributes.cardStackId = null
    modifiedDates[dateString].attributes.unitStart = []
    modifiedDates[dateString].attributes.unitEnd = []
    modifiedDates[dateString].attributes.unitIds = []

    date = dateFns.addDays(date, 1)
  }

  let courseDatesWithoutTrainCar = map(
    courseDates,
    (courseDate) => modifiedDates[courseDate.attributes.dateString] || courseDate,
  )

  return { transferrableArray, courseDatesWithoutTrainCar }
}
